import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { SuspendDialogComponent } from '../../Dialogs/suspend-dialog/suspend-dialog.component';
import { AvStatusChipComponent } from '../../../../Shared/Components/av-status-chip/av-status-chip.component';
import { OrganizationDetailFacade } from '../../../../../Facades/Organizations/organization-detail.facade';
import { DATE_TIME_FORMAT, VALUE_UNRESOLVED } from '../../../../../Consts/backoffice.const';
import {
  BILLING_EMAIL_SOURCE_LABEL,
} from '../../../../../Consts/organization-detail.const';
import { SuspendDialogResult } from '../../../Interfaces/suspend-dialog.interface';

@Component({
  selector: 'av-organization-overview',
  standalone: true,
  imports: [
    DatePipe,
    ReactiveFormsModule,
    MatIconModule,
    MatTooltipModule,
    TranslateModule,
    AvStatusChipComponent,
  ],
  templateUrl: './organization-overview.component.html',
  styleUrl: './organization-overview.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'd-block' },
})
export class OrganizationOverviewComponent {
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _dialog = inject(MatDialog);

  readonly facade = inject(OrganizationDetailFacade);

  readonly dateTimeFormat = DATE_TIME_FORMAT;
  readonly unresolved = VALUE_UNRESOLVED;

  readonly formGroup: FormGroup = this._formBuilder.group({
    billing_email: ['', [Validators.email]],
  });

  readonly billingEmailSourceLabelKey = computed<string>(() => {
    const source = this.facade.organization()?.billing_email_source ?? 'none';

    return BILLING_EMAIL_SOURCE_LABEL[source] ?? source;
  });

  readonly hasOverride = computed<boolean>(
    () => this.facade.organization()?.billing_email_is_inherited === false,
  );

  saveBillingEmail(): void {
    if (!this.formGroup.valid) {
      return;
    }

    const value = String(this.formGroup.value.billing_email ?? '').trim();

    this.facade.saveBillingEmail(value === '' ? null : value);
    this.formGroup.reset({ billing_email: '' });
  }

  clearOverride(): void {
    this.facade.saveBillingEmail(null);
    this.formGroup.reset({ billing_email: '' });
  }

  openSuspend(): void {
    const organization = this.facade.organization();

    if (!organization) {
      return;
    }

    const reference = this._dialog.open(SuspendDialogComponent, {
      data: { organizationName: organization.displayName },
    });

    reference.afterClosed().subscribe((result?: SuspendDialogResult) => this._applySuspend(result));
  }

  reactivate(): void {
    this.facade.reactivate();
  }

  private _applySuspend(result?: SuspendDialogResult): void {
    if (!result) {
      return;
    }

    this.facade.suspend(result.reason);
  }
}
